import React, { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import { useSelector } from "react-redux";
import { Spinner, Alert, Button } from "reactstrap";
import MetaTags from "react-meta-tags";
import "./CentralFindings.scss";
import SlaPolicyCard from "./SlaPolicyCard";

const MODULE_OPTIONS = [
  { value: "", label: "All Modules" },
  { value: "subdomains", label: "Subdomains" },
  { value: "credentials", label: "Credentials" },
  { value: "ports", label: "Ports" },
  { value: "dns", label: "DNS" },
  { value: "whois", label: "WHOIS" },
  { value: "reversedns", label: "Reverse DNS" },
  { value: "reverseip", label: "Reverse IP" },
  { value: "blackbox", label: "Black Box" },
  { value: "certificates", label: "Certificates" },
  { value: "brand", label: "Brand Protection" },
];

const SEVERITY_OPTIONS = [
  { value: "", label: "All Severities" },
  { value: "critical", label: "Critical" },
  { value: "high", label: "High" },
  { value: "medium", label: "Medium" },
  { value: "low", label: "Low" },
  { value: "informational", label: "Informational" },
];

const STATUS_OPTIONS = [
  { value: "", label: "All Statuses" },
  { value: "new", label: "New" },
  { value: "triaged", label: "Triaged" },
  { value: "validated", label: "Validated" },
  { value: "remediated", label: "Remediated" },
  { value: "accepted risk", label: "Accepted Risk" },
  { value: "false positive", label: "False Positive" },
  { value: "reopened", label: "Reopened" },
];

function formatModuleName(mod) {
  if (!mod) return "—";
  const m = String(mod).toLowerCase().trim();
  switch (m) {
    case "subdomains": return "Subdomains";
    case "credentials": return "Credentials";
    case "ports": return "Ports";
    case "dns": return "DNS";
    case "whois": return "WHOIS";
    case "reversedns": return "Reverse DNS";
    case "reverseip": return "Reverse IP";
    case "blackbox": return "Black Box";
    default: return mod.charAt(0).toUpperCase() + mod.slice(1);
  }
}

const CentralFindings = () => {
  const token = localStorage.getItem("token");
  const role = (localStorage.getItem("role") || "read_only").toLowerCase();
  const canWrite = role === "super_admin" || role === "tenant_admin" || role === "analyst";
  // SLA policy and SLA exceptions are tenant-admin only (backend enforces org:manage)
  const isAdmin = role === "super_admin" || role === "tenant_admin";

  const { layoutMode } = useSelector((state) => state.Layout || { layoutMode: "dark" });
  const theme = layoutMode === "light" ? "light" : "dark";

  // Data states
  const [findings, setFindings] = useState([]);
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [summary, setSummary] = useState({
    bySeverity: { critical: 0, high: 0, medium: 0, low: 0, informational: 0 },
    byStatus: {},
    openCount: 0,
    overdueCount: 0,
    total: 0,
  });
  const [loading, setLoading] = useState(false);
  const [feedback, setFeedback] = useState(null);

  // Filters & pagination
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(20);
  const [search, setSearch] = useState("");
  const [searchInput, setSearchInput] = useState("");
  const [selectedModule, setSelectedModule] = useState("");
  const [selectedSeverity, setSelectedSeverity] = useState("");
  const [selectedStatus, setSelectedStatus] = useState("");
  const [overdueOnly, setOverdueOnly] = useState(false);
  const [openOnly, setOpenOnly] = useState(false);

  // Drawer & detail states
  const [selectedFinding, setSelectedFinding] = useState(null);
  const [statusUpdateVal, setStatusUpdateVal] = useState("");
  const [statusNoteVal, setStatusNoteVal] = useState("");
  const [isUpdatingStatus, setIsUpdatingStatus] = useState(false);

  const [ownerInputVal, setOwnerInputVal] = useState("");
  const [isUpdatingOwner, setIsUpdatingOwner] = useState(false);

  const [exceptionType, setExceptionType] = useState("extension");
  const [exceptionDate, setExceptionDate] = useState("");
  const [exceptionReason, setExceptionReason] = useState("");
  const [isSavingException, setIsSavingException] = useState(false);

  const apiBase = process.env.REACT_APP_DEFAULTPATH || "http://localhost:1337/";

  // Fetch findings list
  const fetchFindings = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      params.append("page", page);
      params.append("limit", limit);
      if (search.trim()) params.append("search", search.trim());
      if (selectedModule) params.append("sourceModule", selectedModule);
      if (selectedSeverity) params.append("severity", selectedSeverity);
      if (selectedStatus) params.append("status", selectedStatus);
      if (openOnly) params.append("open", "true");
      if (overdueOnly) params.append("overdue", "true");

      const response = await fetch(`${apiBase}findings?${params.toString()}`, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      });

      const data = await response.json();
      if (data.status) {
        setFindings(data.items || []);
        setTotal(data.total || 0);
        setTotalPages(data.totalPages || 1);
        if (data.summary) {
          setSummary(data.summary);
        }
      } else {
        setFeedback({ type: "danger", message: data.error || "Failed to load findings" });
      }
    } catch (err) {
      console.error("[fetchFindings Error]", err);
      setFeedback({ type: "danger", message: err.message || "Failed to connect to server" });
    } finally {
      setLoading(false);
    }
  }, [apiBase, token, page, limit, search, selectedModule, selectedSeverity, selectedStatus, overdueOnly, openOnly]);

  useEffect(() => {
    fetchFindings();
  }, [fetchFindings]);

  // Debounced search handler
  useEffect(() => {
    const timer = setTimeout(() => {
      setSearch(searchInput);
      setPage(1);
    }, 350);
    return () => clearTimeout(timer);
  }, [searchInput]);

  // Open drawer and sync edit states
  const openDrawer = (finding) => {
    setSelectedFinding(finding);
    setStatusUpdateVal(finding.status || "new");
    setStatusNoteVal("");
    setOwnerInputVal(finding.owner || "");
    setExceptionType("extension");
    setExceptionDate("");
    setExceptionReason("");
  };

  // Close drawer
  const closeDrawer = () => {
    setSelectedFinding(null);
  };

  // PATCH status
  const handleUpdateStatus = async () => {
    if (!selectedFinding || !canWrite) return;
    setIsUpdatingStatus(true);
    try {
      const response = await fetch(`${apiBase}findings/${selectedFinding._id}/status`, {
        method: "PATCH",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          status: statusUpdateVal,
          note: statusNoteVal,
        }),
      });

      const data = await response.json();
      if (data.status && data.finding) {
        setSelectedFinding(data.finding);
        // Update in table list
        setFindings((prev) =>
          prev.map((f) => (f._id === data.finding._id ? data.finding : f))
        );
        setStatusNoteVal("");
        setFeedback({ type: "success", message: `Status updated to '${data.finding.status}'` });
      } else {
        setFeedback({ type: "danger", message: data.error || "Failed to update status" });
      }
    } catch (err) {
      setFeedback({ type: "danger", message: err.message || "Network error" });
    } finally {
      setIsUpdatingStatus(false);
    }
  };

  // PATCH owner
  const handleUpdateOwner = async (clear = false) => {
    if (!selectedFinding || !canWrite) return;
    setIsUpdatingOwner(true);
    const targetOwner = clear ? "" : ownerInputVal.trim();
    try {
      const response = await fetch(`${apiBase}findings/${selectedFinding._id}/owner`, {
        method: "PATCH",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          ownerEmail: targetOwner,
        }),
      });

      const data = await response.json();
      if (data.status && data.finding) {
        setSelectedFinding(data.finding);
        setFindings((prev) =>
          prev.map((f) => (f._id === data.finding._id ? data.finding : f))
        );
        setOwnerInputVal(data.finding.owner || "");
        setFeedback({
          type: "success",
          message: clear ? "Owner cleared" : `Assigned owner to '${data.finding.owner}'`,
        });
      } else {
        setFeedback({ type: "danger", message: data.error || "Failed to update owner" });
      }
    } catch (err) {
      setFeedback({ type: "danger", message: err.message || "Network error" });
    } finally {
      setIsUpdatingOwner(false);
    }
  };

  // POST SLA exception (due date extension or time-boxed accepted risk)
  const handleSlaException = async () => {
    if (!selectedFinding || !isAdmin) return;
    setIsSavingException(true);
    const isExtension = exceptionType === "extension";
    try {
      const response = await fetch(
        `${apiBase}findings/${selectedFinding._id}/${isExtension ? "extend-due-date" : "accept-risk"}`,
        {
          method: "POST",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            [isExtension ? "newDueDate" : "until"]: exceptionDate,
            reason: exceptionReason,
          }),
        }
      );

      const data = await response.json();
      if (data.status && data.finding) {
        setSelectedFinding(data.finding);
        setFindings((prev) =>
          prev.map((f) => (f._id === data.finding._id ? data.finding : f))
        );
        setStatusUpdateVal(data.finding.status);
        setExceptionDate("");
        setExceptionReason("");
        setFeedback({
          type: "success",
          message: isExtension ? "Due date extended" : "Risk accepted for the selected period",
        });
      } else {
        setFeedback({ type: "danger", message: data.error || "Failed to save SLA exception" });
      }
    } catch (err) {
      setFeedback({ type: "danger", message: err.message || "Network error" });
    } finally {
      setIsSavingException(false);
    }
  };

  // Date input bounds for SLA exceptions: tomorrow .. +365 days
  const toDateInput = (d) => d.toISOString().slice(0, 10);
  const minExceptionDate = toDateInput(new Date(Date.now() + 24 * 60 * 60 * 1000));
  const maxExceptionDate = toDateInput(new Date(Date.now() + 365 * 24 * 60 * 60 * 1000));

  // Helper: check overdue
  const isOverdue = (finding) => {
    if (!finding.dueDate) return false;
    const closed = ["remediated", "accepted risk", "false positive", "resolved"];
    if (closed.includes(String(finding.status).toLowerCase())) return false;
    return new Date(finding.dueDate) < new Date();
  };

  // Helper: format date
  const formatDate = (dateStr) => {
    if (!dateStr) return "—";
    try {
      return new Date(dateStr).toLocaleDateString("en-US", {
        month: "short",
        day: "numeric",
        year: "numeric",
      });
    } catch {
      return dateStr;
    }
  };

  const formatDateTime = (dateStr) => {
    if (!dateStr) return "—";
    try {
      return new Date(dateStr).toLocaleString("en-US", {
        month: "short",
        day: "numeric",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      });
    } catch {
      return dateStr;
    }
  };

  const hasActiveFilters =
    Boolean(search) ||
    Boolean(selectedModule) ||
    Boolean(selectedSeverity) ||
    Boolean(selectedStatus) ||
    overdueOnly ||
    openOnly;

  const resetFilters = () => {
    setSearch("");
    setSearchInput("");
    setSelectedModule("");
    setSelectedSeverity("");
    setSelectedStatus("");
    setOverdueOnly(false);
    setOpenOnly(false);
    setPage(1);
  };

  // KPI card active states derived strictly from current filter state
  const isCriticalActive =
    selectedSeverity === "critical" &&
    !selectedStatus &&
    !selectedModule &&
    !search &&
    !overdueOnly &&
    !openOnly;

  const isHighActive =
    selectedSeverity === "high" &&
    !selectedStatus &&
    !selectedModule &&
    !search &&
    !overdueOnly &&
    !openOnly;

  const isOpenActive =
    openOnly &&
    !selectedSeverity &&
    !selectedStatus &&
    !selectedModule &&
    !search &&
    !overdueOnly;

  const isOverdueActive =
    overdueOnly &&
    !selectedSeverity &&
    !selectedStatus &&
    !selectedModule &&
    !search &&
    !openOnly;

  // Clicking a KPI card resets all other filters, then applies only that card's filter
  // Clicking an already-active card toggles it off (clears its filter)
  const handleCriticalCardClick = () => {
    if (isCriticalActive) {
      resetFilters();
    } else {
      setSearch("");
      setSearchInput("");
      setSelectedModule("");
      setSelectedStatus("");
      setOverdueOnly(false);
      setOpenOnly(false);
      setSelectedSeverity("critical");
      setPage(1);
    }
  };

  const handleHighCardClick = () => {
    if (isHighActive) {
      resetFilters();
    } else {
      setSearch("");
      setSearchInput("");
      setSelectedModule("");
      setSelectedStatus("");
      setOverdueOnly(false);
      setOpenOnly(false);
      setSelectedSeverity("high");
      setPage(1);
    }
  };

  const handleOpenCardClick = () => {
    if (isOpenActive) {
      resetFilters();
    } else {
      setSearch("");
      setSearchInput("");
      setSelectedModule("");
      setSelectedSeverity("");
      setSelectedStatus("");
      setOverdueOnly(false);
      setOpenOnly(true);
      setPage(1);
    }
  };

  const handleOverdueCardClick = () => {
    if (isOverdueActive) {
      resetFilters();
    } else {
      setSearch("");
      setSearchInput("");
      setSelectedModule("");
      setSelectedSeverity("");
      setSelectedStatus("");
      setOpenOnly(false);
      setOverdueOnly(true);
      setPage(1);
    }
  };

  return (
    <div className="page-content findings-page-root" data-theme={theme}>
      <MetaTags>
        <title>Central Findings | GuardLogiX</title>
      </MetaTags>

      <div className="findings-container">
        {/* Header */}
        <div className="findings-header">
          <div className="findings-breadcrumbs">
            <Link to="/dashboard">Dashboard</Link>
            <span className="breadcrumb-sep">/</span>
            <span className="breadcrumb-active">Findings</span>
          </div>
          <div className="findings-title-row">
            <div>
              <h1 className="findings-title">Central Findings</h1>
              <p className="findings-subtitle">
                Unified vulnerability and exposure tracking across all attack surface modules
              </p>
            </div>
            <div className="findings-header-actions">
              <button className="btn-refresh" onClick={fetchFindings} title="Refresh findings list">
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M21 12a9 9 0 1 1-9-9c2.5 0 4.8 1 6.5 2.7L21 8" />
                  <path d="M21 3v5h-5" />
                </svg>
                Refresh
              </button>
            </div>
          </div>
        </div>

        {/* Alert Notifications */}
        {feedback && (
          <Alert
            color={feedback.type}
            isOpen={true}
            toggle={() => setFeedback(null)}
            style={{ borderRadius: "var(--radius-md)", margin: 0 }}
          >
            {feedback.message}
          </Alert>
        )}

        <SlaPolicyCard apiBase={apiBase} token={token} isAdmin={isAdmin} onFeedback={setFeedback} />

        {/* KPI Cards Grid */}
        <div className="findings-kpi-grid">
          {/* Critical */}
          <div
            className={`kpi-card card-critical ${isCriticalActive ? "active" : ""}`}
            onClick={handleCriticalCardClick}
          >
            <div className="kpi-left">
              <span className="kpi-label">Critical Findings</span>
              <span className="kpi-value" style={{ color: summary.bySeverity?.critical > 0 ? "#dc2626" : "inherit" }}>
                {summary.bySeverity?.critical || 0}
              </span>
            </div>
            <div className="kpi-icon-wrap kpi-critical">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="m21.73 18-8-14a2 2 0 0 0-3.48 0l-8 14A2 2 0 0 0 4 21h16a2 2 0 0 0 1.73-3Z" />
                <line x1="12" y1="9" x2="12" y2="13" />
                <line x1="12" y1="17" x2="12.01" y2="17" />
              </svg>
            </div>
          </div>

          {/* High */}
          <div
            className={`kpi-card card-high ${isHighActive ? "active" : ""}`}
            onClick={handleHighCardClick}
          >
            <div className="kpi-left">
              <span className="kpi-label">High Findings</span>
              <span className="kpi-value" style={{ color: summary.bySeverity?.high > 0 ? "#ea580c" : "inherit" }}>
                {summary.bySeverity?.high || 0}
              </span>
            </div>
            <div className="kpi-icon-wrap kpi-high">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="12" cy="12" r="10" />
                <line x1="12" y1="8" x2="12" y2="12" />
                <line x1="12" y1="16" x2="12.01" y2="16" />
              </svg>
            </div>
          </div>

          {/* Open */}
          <div
            className={`kpi-card card-open ${isOpenActive ? "active" : ""}`}
            onClick={handleOpenCardClick}
          >
            <div className="kpi-left">
              <span className="kpi-label">Open Findings</span>
              <span className="kpi-value">
                {summary.openCount || 0}
              </span>
            </div>
            <div className="kpi-icon-wrap kpi-open">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                <polyline points="14 2 14 8 20 8" />
                <line x1="16" y1="13" x2="8" y2="13" />
                <line x1="16" y1="17" x2="8" y2="17" />
                <polyline points="10 9 9 9 8 9" />
              </svg>
            </div>
          </div>

          {/* Overdue */}
          <div
            className={`kpi-card card-overdue ${isOverdueActive ? "active" : ""}`}
            onClick={handleOverdueCardClick}
          >
            <div className="kpi-left">
              <span className="kpi-label">Overdue SLA</span>
              <span className="kpi-value" style={{ color: summary.overdueCount > 0 ? "#dc2626" : "inherit" }}>
                {summary.overdueCount || 0}
              </span>
            </div>
            <div className="kpi-icon-wrap kpi-overdue">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="12" cy="12" r="10" />
                <polyline points="12 6 12 12 16 14" />
              </svg>
            </div>
          </div>
        </div>

        {/* Main Table Card */}
        <div className="findings-main-card">
          {/* Toolbar */}
          <div className="findings-toolbar">
            <div className="toolbar-left">
              {/* Search */}
              <div className="search-input-wrap">
                <svg className="search-icon" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <circle cx="11" cy="11" r="8" />
                  <line x1="21" y1="21" x2="16.65" y2="16.65" />
                </svg>
                <input
                  type="text"
                  placeholder="Search title, asset, module, owner..."
                  value={searchInput}
                  onChange={(e) => setSearchInput(e.target.value)}
                />
                {searchInput && (
                  <button className="clear-search-btn" onClick={() => setSearchInput("")} title="Clear search">
                    ✕
                  </button>
                )}
              </div>

              {/* Module Filter */}
              <select
                className="filter-select"
                value={selectedModule}
                onChange={(e) => {
                  setSelectedModule(e.target.value);
                  setPage(1);
                }}
              >
                {MODULE_OPTIONS.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </select>

              {/* Severity Filter */}
              <select
                className="filter-select"
                value={selectedSeverity}
                onChange={(e) => {
                  setSelectedSeverity(e.target.value);
                  setPage(1);
                }}
              >
                {SEVERITY_OPTIONS.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </select>

              {/* Status Filter */}
              <select
                className="filter-select"
                value={selectedStatus}
                onChange={(e) => {
                  setSelectedStatus(e.target.value);
                  setOpenOnly(false);
                  setPage(1);
                }}
              >
                {STATUS_OPTIONS.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </select>

              {/* Overdue Toggle */}
              <button
                className={`overdue-toggle-btn ${overdueOnly ? "active" : ""}`}
                onClick={() => {
                  setOverdueOnly((prev) => !prev);
                  setOpenOnly(false);
                  setPage(1);
                }}
              >
                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <circle cx="12" cy="12" r="10" />
                  <polyline points="12 6 12 12 16 14" />
                </svg>
                Overdue SLA
              </button>

              {/* Reset Filters */}
              {hasActiveFilters && (
                <button className="reset-filters-btn" onClick={resetFilters}>
                  Reset Filters
                </button>
              )}
            </div>
          </div>

          {/* Table Content */}
          <div className="findings-table-wrap">
            <table className="findings-table">
              <thead>
                <tr>
                  <th style={{ width: "130px" }}>Severity</th>
                  <th>Title</th>
                  <th style={{ width: "130px" }}>Module</th>
                  <th style={{ width: "200px" }}>Asset</th>
                  <th style={{ width: "120px" }}>Status</th>
                  <th style={{ width: "170px" }}>Owner</th>
                  <th style={{ width: "140px" }}>Due Date</th>
                  <th style={{ width: "120px" }}>Last Seen</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan="8" style={{ textAlign: "center", padding: "40px" }}>
                      <Spinner color="primary" />
                      <div style={{ marginTop: "10px", color: "var(--fg-secondary)", fontSize: "13px" }}>
                        Loading findings...
                      </div>
                    </td>
                  </tr>
                ) : findings.length === 0 ? (
                  <tr>
                    <td colSpan="8" style={{ textAlign: "center", padding: "48px 20px" }}>
                      <div style={{ color: "var(--fg-secondary)", fontSize: "14px", fontWeight: "600" }}>
                        No findings found
                      </div>
                      <div style={{ color: "var(--fg-tertiary)", fontSize: "12px", marginTop: "4px" }}>
                        {hasActiveFilters
                          ? "Try adjusting or clearing your filters to see more results."
                          : "Run exposure scans to identify and track findings for your organization."}
                      </div>
                    </td>
                  </tr>
                ) : (
                  findings.map((item) => {
                    const overdue = isOverdue(item);
                    return (
                      <tr
                        key={item._id}
                        className={selectedFinding?._id === item._id ? "selected-row" : ""}
                        onClick={() => openDrawer(item)}
                      >
                        <td>
                          <span className={`sev-badge sev-${item.severity || "informational"}`}>
                            <span className="dot" />
                            {item.severity || "Informational"}
                          </span>
                        </td>
                        <td>
                          <div style={{ fontWeight: "600", color: "var(--fg-primary)", lineHeight: "1.4" }}>
                            {item.title}
                          </div>
                        </td>
                        <td>
                          <span className="module-tag">{formatModuleName(item.sourceModule)}</span>
                        </td>
                        <td>
                          <div className="asset-cell" title={item.assetId || "—"}>
                            {item.assetId || "—"}
                          </div>
                        </td>
                        <td>
                          <span className={`status-pill st-${String(item.status || "new").toLowerCase().replace(/\s+/g, "")}`}>
                            {item.status || "new"}
                          </span>
                        </td>
                        <td>
                          <div className="owner-cell" title={item.owner || "Unassigned"}>
                            <span style={{ color: item.owner ? "var(--fg-primary)" : "var(--fg-tertiary)" }}>
                              {item.owner || "Unassigned"}
                            </span>
                          </div>
                        </td>
                        <td>
                          <div style={{ fontSize: "12px", color: overdue ? "#b91c1c" : "var(--fg-secondary)", fontWeight: overdue ? "600" : "400" }}>
                            {formatDate(item.dueDate)}
                          </div>
                          {overdue && <span className="overdue-tag">Overdue</span>}
                        </td>
                        <td>
                          <span style={{ fontSize: "12px", color: "var(--fg-secondary)" }}>
                            {formatDate(item.lastSeen)}
                          </span>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          <div className="findings-pagination">
            <div className="pagination-left">
              <span>Show</span>
              <select
                value={limit}
                onChange={(e) => {
                  setLimit(Number(e.target.value));
                  setPage(1);
                }}
              >
                <option value={10}>10</option>
                <option value={20}>20</option>
                <option value={50}>50</option>
                <option value={100}>100</option>
              </select>
              <span>
                entries · Showing {total > 0 ? (page - 1) * limit + 1 : 0} to{" "}
                {Math.min(page * limit, total)} of {total} findings
              </span>
            </div>

            <div className="pagination-right">
              <button
                disabled={page <= 1}
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                aria-label="Previous page"
              >
                Previous
              </button>
              {Array.from({ length: totalPages }, (_, i) => i + 1)
                .slice(Math.max(0, page - 3), Math.min(totalPages, page + 2))
                .map((p) => (
                  <button
                    key={p}
                    className={p === page ? "active" : ""}
                    onClick={() => setPage(p)}
                  >
                    {p}
                  </button>
                ))}
              <button
                disabled={page >= totalPages}
                onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                aria-label="Next page"
              >
                Next
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Slide-over Detail Drawer */}
      {selectedFinding && (
        <>
          <div className="findings-drawer-backdrop" onClick={closeDrawer} />
          <aside className="findings-drawer">
            {/* Header */}
            <div className="drawer-header">
              <div className="drawer-title-wrap">
                <span className={`sev-badge sev-${selectedFinding.severity || "informational"}`}>
                  <span className="dot" />
                  {selectedFinding.severity || "Informational"}
                </span>
                <h2 className="drawer-title">{selectedFinding.title}</h2>
              </div>
              <button className="drawer-close-btn" onClick={closeDrawer} aria-label="Close drawer">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M18 6 6 18M6 6l12 12" />
                </svg>
              </button>
            </div>

            {/* Body */}
            <div className="drawer-body">
              {/* Management Controls */}
              <div className="mgmt-box">
                <div className="drawer-section-title">Workflow & Assignment</div>
                {!canWrite && (
                  <div style={{ fontSize: "12px", color: "var(--fg-secondary)", marginBottom: "6px" }}>
                    🔒 You have read-only permissions for findings.
                  </div>
                )}

                {/* Status Row */}
                <div className="form-row">
                  <label>Update Status</label>
                  <select
                    value={statusUpdateVal}
                    disabled={!canWrite || isUpdatingStatus}
                    onChange={(e) => setStatusUpdateVal(e.target.value)}
                  >
                    {STATUS_OPTIONS.filter((s) => s.value).map((opt) => (
                      <option key={opt.value} value={opt.value}>
                        {opt.label}
                      </option>
                    ))}
                  </select>
                  <textarea
                    placeholder="Optional note / resolution details (max 500 chars)..."
                    value={statusNoteVal}
                    disabled={!canWrite || isUpdatingStatus}
                    maxLength={500}
                    onChange={(e) => setStatusNoteVal(e.target.value)}
                  />
                  <div className="mgmt-actions">
                    <Button
                      color="primary"
                      size="sm"
                      disabled={!canWrite || isUpdatingStatus || (statusUpdateVal === selectedFinding.status && !statusNoteVal)}
                      onClick={handleUpdateStatus}
                    >
                      {isUpdatingStatus ? <Spinner size="sm" /> : "Save Status"}
                    </Button>
                  </div>
                </div>

                {/* Owner Row */}
                <div className="form-row" style={{ marginTop: "6px", borderTop: "1px solid var(--border-default)", paddingTop: "12px" }}>
                  <label>Owner Email (Must belong to your organization)</label>
                  <div style={{ display: "flex", gap: "8px" }}>
                    <input
                      type="email"
                      placeholder="e.g. security-lead@amenify.com"
                      value={ownerInputVal}
                      disabled={!canWrite || isUpdatingOwner}
                      onChange={(e) => setOwnerInputVal(e.target.value)}
                    />
                    <Button
                      color="primary"
                      size="sm"
                      disabled={!canWrite || isUpdatingOwner || !ownerInputVal.trim()}
                      onClick={() => handleUpdateOwner(false)}
                    >
                      Assign
                    </Button>
                    {selectedFinding.owner && (
                      <Button
                        color="secondary"
                        outline
                        size="sm"
                        disabled={!canWrite || isUpdatingOwner}
                        onClick={() => handleUpdateOwner(true)}
                      >
                        Clear
                      </Button>
                    )}
                  </div>
                </div>

                {/* SLA Exception Row (tenant admin, open findings only) */}
                {isAdmin && !["remediated", "accepted risk", "false positive", "resolved"].includes(String(selectedFinding.status).toLowerCase()) && (
                  <div className="form-row" style={{ marginTop: "6px", borderTop: "1px solid var(--border-default)", paddingTop: "12px" }}>
                    <label htmlFor="sla-exception-type">SLA Exception</label>
                    <select
                      id="sla-exception-type"
                      value={exceptionType}
                      disabled={isSavingException}
                      onChange={(e) => setExceptionType(e.target.value)}
                    >
                      <option value="extension">Extend due date</option>
                      <option value="accepted_risk">Accept risk until date (auto-reopens after)</option>
                    </select>
                    <input
                      type="date"
                      aria-label={exceptionType === "extension" ? "New due date" : "Accept risk until"}
                      min={minExceptionDate}
                      max={maxExceptionDate}
                      value={exceptionDate}
                      disabled={isSavingException}
                      onChange={(e) => setExceptionDate(e.target.value)}
                    />
                    <textarea
                      placeholder="Reason (required, recorded in history and audit log)"
                      value={exceptionReason}
                      disabled={isSavingException}
                      maxLength={500}
                      onChange={(e) => setExceptionReason(e.target.value)}
                    />
                    <div className="mgmt-actions">
                      <Button
                        color="primary"
                        size="sm"
                        disabled={isSavingException || !exceptionDate || exceptionReason.trim().length < 5}
                        onClick={handleSlaException}
                      >
                        {isSavingException ? <Spinner size="sm" /> : exceptionType === "extension" ? "Extend Due Date" : "Accept Risk"}
                      </Button>
                    </div>
                  </div>
                )}

                {selectedFinding.slaException?.type && (
                  <div className="sla-exception-info">
                    <strong>
                      {selectedFinding.slaException.type === "extension" ? "Due date extended" : "Risk accepted"}
                    </strong>{" "}
                    {selectedFinding.slaException.type === "extension"
                      ? `to ${formatDate(selectedFinding.slaException.until)}`
                      : `until ${formatDate(selectedFinding.slaException.until)}`}
                    {" "}by {selectedFinding.slaException.by} on {formatDate(selectedFinding.slaException.at)}.
                    <br />
                    Reason: {selectedFinding.slaException.reason}
                  </div>
                )}
              </div>

              {/* Meta Grid */}
              <div>
                <div className="drawer-section-title">Overview</div>
                <div className="meta-grid">
                  <div className="meta-item">
                    <div className="meta-k">Module</div>
                    <div className="meta-v">
                      <span className="module-tag">{formatModuleName(selectedFinding.sourceModule)}</span>
                    </div>
                  </div>
                  <div className="meta-item">
                    <div className="meta-k">Asset ID</div>
                    <div className="meta-v">{selectedFinding.assetId || "—"}</div>
                  </div>
                  <div className="meta-item">
                    <div className="meta-k">Status</div>
                    <div className="meta-v" style={{ textTransform: "capitalize" }}>
                      {selectedFinding.status}
                    </div>
                  </div>
                  <div className="meta-item">
                    <div className="meta-k">Owner</div>
                    <div className="meta-v">{selectedFinding.owner || "Unassigned"}</div>
                  </div>
                  <div className="meta-item">
                    <div className="meta-k">Due Date (SLA)</div>
                    <div className="meta-v">
                      {formatDate(selectedFinding.dueDate)}
                      {isOverdue(selectedFinding) && (
                        <span className="overdue-tag">Overdue</span>
                      )}
                    </div>
                  </div>
                  <div className="meta-item">
                    <div className="meta-k">First Seen</div>
                    <div className="meta-v">{formatDateTime(selectedFinding.firstSeen)}</div>
                  </div>
                  <div className="meta-item">
                    <div className="meta-k">Last Seen</div>
                    <div className="meta-v">{formatDateTime(selectedFinding.lastSeen)}</div>
                  </div>
                  <div className="meta-item">
                    <div className="meta-k">Deduplication Key</div>
                    <div className="meta-v" style={{ fontSize: "11px", wordBreak: "break-all" }}>
                      {selectedFinding.dedupeKey}
                    </div>
                  </div>
                </div>
              </div>

              {/* Link to the full Black Box finding (PoC, remediation, retest) */}
              {selectedFinding.sourceModule === "blackbox" && selectedFinding.evidence?.taskDataId && (
                <Link
                  to={`/exposedblackbox/finding/${selectedFinding.evidence.taskDataId}/${selectedFinding.evidence.findingId || selectedFinding.evidence.findingIndex}`}
                  onClick={closeDrawer}
                  style={{ fontSize: "13px", fontWeight: 600, color: "var(--brand-primary)" }}
                >
                  Open in Black Box (proof of concept, remediation, retest) →
                </Link>
              )}

              {/* Description */}
              {selectedFinding.description && (
                <div>
                  <div className="drawer-section-title">Description</div>
                  <div className="desc-box">{selectedFinding.description}</div>
                </div>
              )}

              {/* Evidence */}
              {selectedFinding.evidence && (
                <div>
                  <div className="drawer-section-title">Evidence & Scan Metadata</div>
                  <pre className="evidence-pre">
                    {JSON.stringify(selectedFinding.evidence, null, 2)}
                  </pre>
                </div>
              )}

              {/* History Timeline */}
              <div>
                <div className="drawer-section-title">Activity & History</div>
                {selectedFinding.history && selectedFinding.history.length > 0 ? (
                  <div className="history-timeline">
                    {selectedFinding.history.map((h, idx) => (
                      <div key={idx} className="timeline-item">
                        <div className="timeline-header">
                          <span className="timeline-change">
                            {h.from ? `${h.from} → ${h.to}` : `Created as ${h.to}`}
                          </span>
                          <span className="timeline-time">{formatDateTime(h.at)}</span>
                        </div>
                        <div className="timeline-by">By {h.by || "system"}</div>
                        {h.note && <div className="timeline-note">{h.note}</div>}
                      </div>
                    ))}
                  </div>
                ) : (
                  <div style={{ color: "var(--fg-tertiary)", fontSize: "12px", fontStyle: "italic" }}>
                    No recorded history events yet.
                  </div>
                )}
              </div>
            </div>
          </aside>
        </>
      )}
    </div>
  );
};

export default CentralFindings;
